import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Global } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { Base_URL } from './constants/api';
import IELTSBottomNavigation from './IELTSBottomNavigation';
import IELTSHeader from './IELTSHeader';
import IELTSBandScore from './IELTSBandScore';
import { 
  ieltsPassageGlobalStyles, 
  ieltsPassageInlineStyles,
  questionNumberStyles,
  questionNumberInlineStyles
} from './styles/passageStyles';
import { themeColors } from './styles/theme';
import {
  MainContainer,
  ContentContainer,
  QuestionsContainer,
} from './styles/componentStyles';
import IELTSAudioPlayer from './components/IELTSAudioPlayer';
import {
  extractBlankNumberMappingAndCleanHtml,
  processPassageText,
  findQuestionNumberFromSiblings,
} from './utils/passageHelpers';
import { markTFNInfoTables } from './utils/htmlTableMarkers';
import {
  getSectionQuestionNumbers as getSectionQuestionNumbersUtil,
  createQuestionNumberToDbIdMap,
  buildAnswersArray,
  checkSkippedQuestions,
  extractQuestionNumberFromNode,
} from './utils/questionHelpers';
import { hydrateMCQContainers } from './utils/mcqHydration';
import { formatTime } from './utils/timeHelpers';
import { useAudioPlayer } from './hooks/useAudioPlayer';

const ListeningTestUI = ({ mode = 'practice', timeLimit = 30, onComplete, sections = [], audioUrl, testId, userId = 3, selectedSectionIndices, audio_url, groupId, fullTestAttemptId, showSaveExit = false, apiResult: apiResultProp, onFullTestPause }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSimulationMode = mode === 'simulation';
  const [currentPart, setCurrentPart] = useState(1);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit === 'Unlimited' ? 'Unlimited' : timeLimit * 60);
  const [cleanedPassageHtml, setCleanedPassageHtml] = useState('');
  const [blankNumbersInOrder, setBlankNumbersInOrder] = useState([]);
  const [tableBlankNumbersInOrder, setTableBlankNumbersInOrder] = useState([]);
  const [selectionNumbersInOrder, setSelectionNumbersInOrder] = useState([]);
  const [tableSelectionNumbersInOrder, setTableSelectionNumbersInOrder] = useState([]);
  const [selectionNumbersFinal, setSelectionNumbersFinal] = useState([]);
  const [blankIdToNumberMap, setBlankIdToNumberMap] = useState({});
  const [selectionIdToNumberMap, setSelectionIdToNumberMap] = useState({});
  const [allAnswers, setAllAnswers] = useState({});
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [apiResult, setApiResult] = useState(null);
  const answersRef = useRef({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [showRedForUnanswered, setShowRedForUnanswered] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [testStartTime] = useState(() => new Date().toISOString().slice(0, 19).replace('T', ' '));
  const [testEndTime, setTestEndTime] = useState(null);
  
  const isTestMode = mode === 'test';
  const isReviewMode = mode === 'review';

  useEffect(() => {
    if (isReviewMode && apiResultProp) {
      setApiResult(apiResultProp);
    }
  }, [isReviewMode, apiResultProp]);

  const getCurrentPartAudioUrl = useCallback(() => {
    const currentSection = sections[currentPart - 1];
    if (currentSection && currentSection.audio_url) {
      return currentSection.audio_url;
    }
    return audio_url || audioUrl;
  }, [currentPart, sections, audio_url, audioUrl]);

  // If only the first section has audio, use that single URL for all sections so
  // playback continues without stopping/reloading when switching tabs.
  const firstSectionAudioUrl = useMemo(() => {
    const first = sections[0];
    return (first && first.audio_url) ? first.audio_url : (audio_url || audioUrl);
  }, [sections, audio_url, audioUrl]);

  const onlyFirstSectionHasAudio = useMemo(() => {
    if (!firstSectionAudioUrl) return false;
    return sections.every((s, i) => i === 0 || !(s && s.audio_url));
  }, [sections, firstSectionAudioUrl]);

  const audioSrc = onlyFirstSectionHasAudio ? firstSectionAudioUrl : getCurrentPartAudioUrl();

  const {
    audioRef,
    isPlaying,
    isMuted,
    audioProgress,
    audioLoaded,
    currentTime,
    duration,
    togglePlay,
    skipForward,
    skipBackward,
    toggleMute,
    handleTimeUpdate,
    handleAudioLoaded,
    handleAudioEnded,
  } = useAudioPlayer(audioSrc, isSimulationMode, isReviewMode);

  // Determine which parts to show in the footer
  const partNumbers = selectedSectionIndices
    ? selectedSectionIndices.map(i => i + 1)
    : sections.map((_, idx) => idx + 1);

  // Get section question numbers using utility
  const getSectionQuestionNumbers = useCallback((sectionIdx) => {
    const section = sections[sectionIdx];
    return getSectionQuestionNumbersUtil(section);
  }, [sections]);

  // Helper for current section
  const getCurrentSectionQuestionNumbers = useCallback(() => {
    return getSectionQuestionNumbers(currentPart - 1);
  }, [getSectionQuestionNumbers, currentPart]);

  // Sync showRedForUnanswered with showSkipWarning
  useEffect(() => {
    setShowRedForUnanswered(showSkipWarning);
  }, [showSkipWarning]);

  // Clear skippedQuestions when showRedForUnanswered becomes false
  useEffect(() => {
    if (!showRedForUnanswered) {
      setSkippedQuestions([]);
    }
  }, [showRedForUnanswered]);

  // Helper to check if a question is incorrect (for results mode - when test is submitted)
  // Also shows unanswered questions as red when warning modal is displayed
  const isQuestionIncorrect = useCallback((sectionIdx, questionNumber) => {
    // If test has been submitted, use analytics from backend
    if (apiResult && apiResult.response && apiResult.response.analytics) {
      const analytics = apiResult.response.analytics[questionNumber];
      return analytics && analytics.isCorrect === false;
    }
    
    // If warning modal is shown, show red circles for unanswered questions
    if (showRedForUnanswered) {
      const isAnswered = allAnswers[questionNumber] !== undefined && 
                        allAnswers[questionNumber] !== '' && 
                        allAnswers[questionNumber] !== null;
      return !isAnswered; // Return true if unanswered (will show red)
    }
    
    // Otherwise, don't show red circles
    return false;
  }, [apiResult, showRedForUnanswered, allAnswers]);

  // Handle part change
  const handlePartChange = (part) => {
    setCurrentPart(part);
  };

  // Create mapping from question numbers to database question IDs
  const questionNumberToDbIdMap = useMemo(() => {
    return createQuestionNumberToDbIdMap(sections);
  }, [sections]);

  // Check for skipped questions
  const checkSkippedQuestionsCallback = useCallback(() => {
    const skipped = checkSkippedQuestions(sections, allAnswers, getSectionQuestionNumbers);
    setSkippedQuestions(skipped);
    return skipped;
  }, [sections, allAnswers, getSectionQuestionNumbers]);

  const handleApiSubmit = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setIsTimerRunning(false);
    setIsSubmitting(true);
    
    const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setTestEndTime(endTime);
    
    // Collect all answers from all sections (use allAnswers)
    const answers = { ...allAnswers };
    Object.assign(answers, answersRef.current);
    // Build answers array for API
    const answersArray = buildAnswersArray(answers, questionNumberToDbIdMap);
    // Build payload
    const payload = {
      testId: Number(testId),
      userId: Number(userId),
      answers: answersArray,
      start_time: testStartTime,
      end_time: endTime,
      ...(groupId ? { groupId } : {}),
      ...(groupId && fullTestAttemptId ? { fullTestAttemptId } : {}),
      ...(groupId ? { is_speaking_optional: localStorage.getItem('is_speaking_optional') === 'true' } : {}),
    };
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/saveTestAttempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data && data.response) {
        setApiResult(data); // Save the whole response
        if (onComplete) onComplete(data);
      } else {
        console.error('Invalid response format:', data);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
    }
    setIsSubmitting(false);
  };

  const handleSubmitWithWarning = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const skipped = checkSkippedQuestionsCallback();
    if (skipped.length > 0) {
      setShowSkipWarning(true);
    } else {
      handleApiSubmit();
    }
  };

  const handleSubmit = () => {
    handleSubmitWithWarning();
  };

  const handleSaveAndExit = () => {
    navigate('/ielts-dashboard');
  };

  useEffect(() => {
    let timer;
    if (isTimerRunning && timeRemaining !== 'Unlimited') {
      timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Auto-submit when time is up
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerRunning, timeRemaining]);

  // Handler for dropdown, blank input changes (store in ref and allAnswers)
  const handleHydratedAnswerChange = (questionKey, value) => {
    setAllAnswers(prev => ({ ...prev, [questionKey]: value }));
    answersRef.current[questionKey] = value;
  };

  // Helper function to get correct answer from multiple sources (for review mode)
  const getCorrectAnswer = useCallback((questionNumber) => {
    // First, try analytics (if question was answered)
    const analyticsAnswer = apiResult?.response?.analytics?.[questionNumber]?.correctAnswer;
    if (analyticsAnswer !== undefined && analyticsAnswer !== null) {
      return analyticsAnswer;
    }
    
    // Second, try to extract from sections (sample_answer or data-answers)
    if (sections && sections.length > 0) {
      for (const section of sections) {
        if (section.passage_text) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(section.passage_text, 'text/html');
          const questionBoxes = doc.querySelectorAll('.question-box');
          
          for (const box of questionBoxes) {
            const answersAttr = box.getAttribute('data-answers');
            if (answersAttr) {
              try {
                const answers = JSON.parse(answersAttr);
                if (answers[questionNumber] !== undefined) {
                  return answers[questionNumber];
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
        
        // Try sample_answer from questions
        if (section.questions && section.questions.length > 0) {
          for (const question of section.questions) {
            if (question.sample_answer) {
              try {
                let sampleAnswer = question.sample_answer;
                if (typeof sampleAnswer === 'string') {
                  sampleAnswer = JSON.parse(sampleAnswer);
                }
                
                // Handle array format: [{"questionNumber":"1","answer":"500"},...]
                if (Array.isArray(sampleAnswer)) {
                  const entry = sampleAnswer.find(item => 
                    item && (item.questionNumber === questionNumber.toString() || 
                            item.questionNumber === parseInt(questionNumber) ||
                            item.question_number === questionNumber.toString() ||
                            item.question_number === parseInt(questionNumber))
                  );
                  if (entry && entry.answer) {
                    return entry.answer;
                  }
                } 
                // Handle object format: {"1":"500","2":"two"}
                else if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
                  if (sampleAnswer[questionNumber.toString()] !== undefined) {
                    return sampleAnswer[questionNumber.toString()];
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
      }
    }
    
    return null;
  }, [apiResult, sections]);


  // Add a function to handle question selection
  const handleQuestionSelect = (questionId) => {
    // Find the question element and scroll to it
    const questionElement = document.getElementById(`question-${questionId}`);
    if (questionElement) {
      questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    if (sections && sections[currentPart - 1] && sections[currentPart - 1].passage_text) {
      // Extract numbers for each blank in order (non-table)
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections[currentPart - 1].passage_text, 'text/html');
      const numbers = [];
      const tableNumbers = [];
      // For table blanks
      doc.querySelectorAll('table .tiptap-input-field').forEach(span => {
        const qNum = extractQuestionNumberFromNode(span);
        tableNumbers.push(qNum);
      });
      // For non-table blanks
      doc.querySelectorAll('p').forEach(p => {
        let numberMatch = p.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/);
        let number = numberMatch ? (numberMatch[2] || numberMatch[3]) : '';
        // For each blank in this <p>
        p.querySelectorAll('.tiptap-input-field').forEach(span => {
          // Only push if not inside a table
          if (!span.closest('table')) {
            numbers.push(number);
          }
        });
      });
      setBlankNumbersInOrder(numbers);
      setTableBlankNumbersInOrder(tableNumbers);
      // Extract numbers for selection fields in order (not in table)
      const selectionNumbers = [];
      // Non-question-box paragraphs
      doc.querySelectorAll('p').forEach(p => {
        const pHTML = p.innerHTML;
        const markers = [];
        const selects = [];
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let m;
        while ((m = markerRegex.exec(pHTML)) !== null) {
          markers.push({ number: m[2] || m[3], index: m.index });
        }
        p.querySelectorAll('.selection-field').forEach(span => {
          if (!span.closest('table')) {
            const html = span.outerHTML;
            const idx = pHTML.indexOf(html);
            if (idx !== -1) selects.push({ index: idx });
          }
        });
        selects.forEach(sel => {
          let matched = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < sel.index) { matched = markers[i]; break; }
          }
          selectionNumbers.push(matched ? matched.number : '');
        });
      });
      // For table dropdowns
      const tableSelectionNumbers = [];
      doc.querySelectorAll('table .selection-field').forEach(span => {
        const qNum = extractQuestionNumberFromNode(span);
        tableSelectionNumbers.push(qNum);
      });
      setSelectionNumbersInOrder(selectionNumbers);
      setTableSelectionNumbersInOrder(tableSelectionNumbers);
      setSelectionNumbersFinal(selectionNumbers);
      // Clean the HTML and capture per-element mappings (same logic as Reading)
      const { cleanedHtml, blankToNumber, selectionToNumber } = extractBlankNumberMappingAndCleanHtml(sections[currentPart - 1].passage_text);
      setBlankIdToNumberMap(blankToNumber || {});
      setSelectionIdToNumberMap(selectionToNumber || {});
      setCleanedPassageHtml(markTFNInfoTables(processPassageText(cleanedHtml)));
    } else {
      setSelectionNumbersInOrder([]);
      setTableSelectionNumbersInOrder([]);
      setSelectionNumbersFinal([]);
      setBlankNumbersInOrder([]);
      setTableBlankNumbersInOrder([]);
      setCleanedPassageHtml('');
    }
  }, [sections, currentPart]);

  useEffect(() => {
    const el = document.querySelector('.ielts-passage-questions');
    if (el) {
      // Hydrate blanks
      const blanks = el.querySelectorAll('span[data-blank-id]');
      // Detect duplicate legacy ids (e.g., many blanks sharing same id)
      const blankIdCounts = {};
      blanks.forEach((span) => {
        const id = span.getAttribute('data-blank-id');
        if (id) {
          blankIdCounts[id] = (blankIdCounts[id] || 0) + 1;
        }
      });
      let tableIdx = 0;
      let normalIdx = 0;
      blanks.forEach((span) => {
        const isInTable = !!span.closest('table');
        const blankId = span.getAttribute('data-blank-id');
        const mapped = blankId && blankIdToNumberMap && blankIdToNumberMap[blankId];
        let qNum;
        if (isInTable) {
          // Prefer marker-based number for question-box table blanks (e.g. 1, 2 instead of "1-2")
          qNum = mapped || tableBlankNumbersInOrder[tableIdx] || '';
          if (!mapped) tableIdx++;
        } else {
          const isDuplicateId = blankId && blankIdCounts[blankId] > 1;
          // If legacy duplicate IDs are present, fall back to order-based mapping
          qNum = (!isDuplicateId && mapped)
            ? mapped
            : (blankNumbersInOrder[normalIdx] || '');
          normalIdx++;
        }
        
        // Fallback: Check parent question-box for question number (single number only; range "1-2" stays as placeholder)
        if (!qNum || !/^\d+$/.test(qNum)) {
          const questionBox = span.closest('.question-box');
          if (questionBox) {
            const boxQNum = questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber');
            if (boxQNum && /^\d+$/.test(boxQNum)) {
              qNum = boxQNum;
            }
          }
        }
        
        // Get analytics if available
        const analytics = apiResult?.response?.analytics?.[qNum];
        const isCorrect = analytics?.isCorrect;
        let correctAnswer = analytics?.correctAnswer;
        // For review mode, try to get correct answer from multiple sources
        if (!correctAnswer && apiResult) {
          correctAnswer = getCorrectAnswer(qNum);
        }
        const userAnswer = analytics?.userAnswer;
        // Use userAnswer from analytics if available, otherwise use allAnswers
        const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null) 
          ? userAnswer 
          : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
        if (!span.querySelector('input')) {
          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'fill-blank-input';
          input.value = displayValue;
          input.placeholder = qNum;
          input.style.width = '90px';
          input.style.margin = '0 4px';
          input.style.border = '1px solid #996CFE';
          input.style.borderRadius = '4px';
          input.style.padding = '2px 6px';
          input.style.fontSize = '15px';
          input.style.color = '#333';
          if (apiResult) {
            input.disabled = true;
            input.style.backgroundColor = isCorrect ? '#E8F5E9' : '#FFF3F3';
            input.style.borderColor = isCorrect ? '#37965E' : '#E53935';
          }
          input.oninput = (e) => {
            handleHydratedAnswerChange(qNum, e.target.value);
          };
          span.innerHTML = '';
          span.appendChild(input);
          // Show correct answer if wrong OR unanswered (for review mode)
          if (apiResult && correctAnswer !== undefined && correctAnswer !== null && (!isCorrect || (!userAnswer && isReviewMode))) {
            const correctBox = document.createElement('span');
            correctBox.setAttribute('data-correct-answer', 'true');
            correctBox.textContent = `Correct: ${correctAnswer}`;
            correctBox.style.marginLeft = '8px';
            correctBox.style.fontSize = '13px';
            correctBox.style.color = '#37965E';
            correctBox.style.fontWeight = '600';
            correctBox.style.background = '#E8F5E9';
            correctBox.style.borderRadius = '4px';
            correctBox.style.padding = '2px 8px';
            correctBox.style.border = '1px solid #37965E';
            span.appendChild(correctBox);
          }
        } else {
          // If input already exists, update its value
          const input = span.querySelector('input');
          if (input) {
            input.value = displayValue;
            if (apiResult) {
              input.disabled = true;
              input.style.backgroundColor = isCorrect ? '#E8F5E9' : '#FFF3F3';
              input.style.borderColor = isCorrect ? '#37965E' : '#E53935';
            }
            
            // Handle correct answer box for existing inputs
            // Remove existing correct answer box if any
            const existingCorrectBox = span.querySelector('span[data-correct-answer]');
            if (existingCorrectBox) {
              existingCorrectBox.remove();
            }
            
            // Add correct answer box if needed (wrong OR unanswered in review mode)
            if (apiResult && correctAnswer !== undefined && correctAnswer !== null && (!isCorrect || (!userAnswer && isReviewMode))) {
              const correctBox = document.createElement('span');
              correctBox.setAttribute('data-correct-answer', 'true');
              correctBox.textContent = `Correct: ${correctAnswer}`;
              correctBox.style.marginLeft = '8px';
              correctBox.style.fontSize = '13px';
              correctBox.style.color = '#37965E';
              correctBox.style.fontWeight = '600';
              correctBox.style.background = '#E8F5E9';
              correctBox.style.borderRadius = '4px';
              correctBox.style.padding = '2px 8px';
              correctBox.style.border = '1px solid #37965E';
              span.appendChild(correctBox);
            }
          }
        }
      });

      // Hydrate selection fields
      const selectionFields = el.querySelectorAll('span.selection-field');
      // Detect duplicate selection ids as well
      const selectionIdCounts = {};
      selectionFields.forEach((span) => {
        const id = span.getAttribute('id');
        if (id) {
          selectionIdCounts[id] = (selectionIdCounts[id] || 0) + 1;
        }
      });
      let selectionIdx = 0;
      let tableSelectionIdx = 0;
      selectionFields.forEach((span) => {
        const optionsAttr = span.getAttribute('data-options') || span.getAttribute('options') || '';
        const options = optionsAttr.split(',').map(opt => opt.trim());
        const isInTable = !!span.closest('table');
        const selectionId = span.getAttribute('id');
        const mapped = selectionId && selectionIdToNumberMap && selectionIdToNumberMap[selectionId];
        let qNum = '';
        if (isInTable) {
          // Prefer marker-based number for question-box table dropdowns (e.g. 1, 2 instead of "1-2")
          qNum = mapped || tableSelectionNumbersInOrder[tableSelectionIdx] || '';
          if (!mapped) tableSelectionIdx++;
        } else {
          const isDuplicateId = selectionId && selectionIdCounts[selectionId] > 1;
          qNum = (!isDuplicateId && mapped)
            ? mapped
            : (selectionNumbersFinal[selectionIdx] || '');
          selectionIdx++;
        }
        
        // Fallback: Check parent question-box for question number (single number only)
        if (!qNum || !/^\d+$/.test(qNum)) {
          const questionBox = span.closest('.question-box');
          if (questionBox) {
            const boxQNum = questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber');
            if (boxQNum && /^\d+$/.test(boxQNum)) {
              qNum = boxQNum;
            }
          }
        }
        
        // Question numbers removed - no longer displaying question numbers for selection fields
        // Get analytics if available
        const analytics = apiResult?.response?.analytics?.[qNum];
        const isCorrect = analytics?.isCorrect;
        // Get correct answer from multiple sources
        let correctAnswer = analytics?.correctAnswer;
        if (!correctAnswer && apiResult) {
          correctAnswer = getCorrectAnswer(qNum);
        }
        const userAnswer = analytics?.userAnswer;
        // Use userAnswer from analytics if available, otherwise use allAnswers
        const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null) 
          ? userAnswer 
          : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
        const select = document.createElement('select');
        select.className = 'dropdown-selection-field';
        select.style.border = '1px solid #996CFE';
        select.style.borderRadius = '4px';
        select.style.padding = '2px 6px';
        select.style.fontSize = '15px';
        select.style.color = '#333';
        select.style.marginRight = '4px';
        if (apiResult) {
          select.disabled = true;
          select.style.backgroundColor = isCorrect ? '#E8F5E9' : '#FFF3F3';
          select.style.borderColor = isCorrect ? '#37965E' : '#E53935';
        }
        // Add a default "Select" option
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = 'Select';
        defaultOption.disabled = true;
        defaultOption.selected = !displayValue;
        select.appendChild(defaultOption);
        options.forEach(opt => {
          const optElem = document.createElement('option');
          optElem.value = opt;
          optElem.textContent = opt;
          optElem.selected = opt === displayValue;
          select.appendChild(optElem);
        });
        select.value = displayValue;
        select.onchange = (e) => handleHydratedAnswerChange(qNum, e.target.value);
        span.innerHTML = '';
        span.appendChild(select);
        // Show correct answer if wrong OR unanswered (for review mode)
        if (apiResult && correctAnswer !== undefined && correctAnswer !== null && (!isCorrect || (!userAnswer && isReviewMode))) {
          const correctBox = document.createElement('span');
          correctBox.setAttribute('data-correct-answer', 'true');
          correctBox.textContent = `Correct: ${correctAnswer}`;
          correctBox.style.marginLeft = '8px';
          correctBox.style.fontSize = '13px';
          correctBox.style.color = '#37965E';
          correctBox.style.fontWeight = '600';
          correctBox.style.background = '#E8F5E9';
          correctBox.style.borderRadius = '4px';
          correctBox.style.padding = '2px 8px';
          correctBox.style.border = '1px solid #37965E';
          span.appendChild(correctBox);
        }
      });
      
      // Hydrate MCQ containers (same as ReadingTestUI)
      // Hydrate MCQ containers using shared function
      hydrateMCQContainers(el, {
        allAnswers,
        apiResult,
        onAnswerChange: handleHydratedAnswerChange
      });
    }
  }, [cleanedPassageHtml, currentPart, allAnswers, apiResult, getCorrectAnswer, isReviewMode]);

  // Add useEffect to update question numbers when passage changes
  useEffect(() => {
    if (cleanedPassageHtml) {
      // Wait for DOM to update
      setTimeout(() => {
        const numbers = getCurrentSectionQuestionNumbers();
        // Force re-render by updating state
        setBlankNumbersInOrder(prev => [...prev]);
      }, 100);
    }
  }, [cleanedPassageHtml, currentPart]);

  return (
    <>
      <Global styles={ieltsPassageGlobalStyles} />
      {/* Loading overlay with fade effect */}
      {isSubmitting && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(30, 16, 60, 0.25)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.3s',
        }}>
          <CircularProgress size={64} sx={{ color: '#996CFE' }} />
        </Box>
      )}
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      margin: '0 auto',
      boxSizing: 'border-box',
      height: isReviewMode ? 'auto' : '100vh', 
      minHeight: isReviewMode ? 'unset' : '100vh',
      overflow: isReviewMode ? 'visible' : 'hidden'
    }}>
      {/* Show Header for practice/simulation modes, Band Score for test/review modes */}
      {!isTestMode && !isReviewMode && (
        <IELTSHeader
          timerText={formatTime(timeRemaining) + ' remaining'}
          // middleContent={!isSimulationMode ? <AudioPlayingIndicator isPlaying={isPlaying} /> : null}
          showReview={false}
          showSaveExit={showSaveExit && !apiResult}
          showSubmit={true}
          apiResult={apiResult}
          groupId={groupId}
          onReview={() => {}}
          onSaveExit={handleSaveAndExit}
          onSubmit={handleSubmitWithWarning}
          onSubmitNavigate="/ielts-results"
          onFullTestPause={onFullTestPause}
        />
      )}

      <IELTSAudioPlayer
        audioProgress={audioProgress}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        isMuted={isMuted}
        togglePlay={togglePlay}
        skipForward={skipForward}
        skipBackward={skipBackward}
        toggleMute={toggleMute}
        isSimulationMode={isSimulationMode}
        isReviewMode={isReviewMode}
        sx={{
          marginBottom: isReviewMode ? '24px' : 0
        }}
      />

      {/* Band Score and Stats Display - For test/review modes */}
      {(isTestMode || isReviewMode) && (
        <IELTSBandScore apiResult={apiResult} showCorrectIncorrect={true} />
      )}

      {/* Bottom Navigation - placed differently for test/review modes */}
      {(isTestMode || isReviewMode) && (
        <IELTSBottomNavigation
          key={`nav-${apiResult ? 'submitted' : 'active'}`}
          parts={partNumbers.map((part) => ({
            part,
            sectionIdx: part - 1,
            part_name: sections[part - 1]?.part_name || `Part ${part}`
          }))}
          currentPart={currentPart}
          onPartChange={handlePartChange}
          getQuestionNumbers={(sectionIdx) => getSectionQuestionNumbers(sectionIdx)}
          allAnswers={allAnswers}
          isQuestionIncorrect={isQuestionIncorrect}
          skippedQuestions={[]}
          onQuestionClick={handleQuestionSelect}
          getPartName={(part) => part.part_name || `Part ${part.part || part}`}
          sections={sections}
          fixed={false}
        />
      )}

      {/* Audio element - always present but controls are hidden based on mode */}
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleAudioLoaded}
        onEnded={handleAudioEnded}
        autoPlay={isSimulationMode}
        style={{ display: 'none' }} // Ensure it's never visible
      />

      {/* Main Content */}
      <MainContainer 
        variant={isTestMode || isReviewMode ? 'test2' : undefined}
        sx={{ 
          flex: 1, 
          minHeight: 0, 
          overflow: isReviewMode ? 'visible' : 'hidden', 
          paddingBottom: (isTestMode || isReviewMode) ? 0 : '77px' 
        }}
      >
        <ContentContainer>
          <Box sx={{ 
            flex: 1, 
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: themeColors.white
          }}>
              <QuestionsContainer sx={{
                flex: 1,
                overflowY: 'auto',
                p: { xs: 2, sm: 4, md: 6 },
                borderRadius: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                paddingBottom: { xs: '50px', sm: '50px', md: '50px' }
              }}>
                {/* Result Summary Modal/Box (now inside QuestionsContainer, above questions) */}
                {/* Band card should only show in practice/simulation mode, not on results page (review mode) */}
                {!isReviewMode && (
                  <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mt: 2 }}>
                    <IELTSBandScore 
                      apiResult={apiResult} 
                      showCorrectIncorrect={true}
                    />
                  </Box>
                )}
                <style>{ieltsPassageInlineStyles}</style>
                {(isTestMode || isReviewMode) && <style>{questionNumberInlineStyles}</style>}
                <div
                  className="ielts-passage-questions"
                  dangerouslySetInnerHTML={{ __html: cleanedPassageHtml }}
                />
              </QuestionsContainer>
          </Box>
        </ContentContainer>
      </MainContainer>
      {/* Bottom Navigation - For practice/simulation modes */}
      {!isTestMode && !isReviewMode && (
        <IELTSBottomNavigation
        key={`nav-${showRedForUnanswered ? 'warning' : 'normal'}-${apiResult ? 'submitted' : 'active'}`}
        parts={partNumbers.map((part) => ({
          part,
          sectionIdx: part - 1,
          part_name: sections[part - 1]?.part_name || `Part ${part}`
        }))}
        currentPart={currentPart}
        onPartChange={handlePartChange}
        getQuestionNumbers={(sectionIdx) => getSectionQuestionNumbers(sectionIdx)}
        allAnswers={allAnswers}
        isQuestionIncorrect={isQuestionIncorrect}
        skippedQuestions={skippedQuestions}
        onQuestionClick={handleQuestionSelect}
        getPartName={(part) => part.part_name || `Part ${part.part || part}`}
        sections={sections}
        />
      )}

      {/* Skip Warning Dialog */}
      <Dialog
        open={showSkipWarning}
        onClose={() => {
          setShowSkipWarning(false);
          setShowRedForUnanswered(false);
          setSkippedQuestions([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          color: '#FF5252', 
          fontWeight: 600,
          fontSize: '20px',
          pb: 1
        }}>
          ⚠️ Unattempted Questions
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 2 }}>
            You have some unattempted questions. Are you sure you want to submit?
          </Typography>
          
          <Box sx={{ backgroundColor: '#FFF3F3', p: 2, borderRadius: '8px', border: '1px solid #FF5252' }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              These questions will be marked as incorrect if you submit now.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={() => {
              setShowSkipWarning(false);
              setShowRedForUnanswered(false);
              setSkippedQuestions([]);
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#E0E0E0',
              color: '#666',
              '&:hover': { 
                borderColor: '#996CFE',
                backgroundColor: '#F5F0FF'
              }
            }}
          >
            Go Back
          </Button>
          <Button
            onClick={() => {
              setShowSkipWarning(false);
              handleApiSubmit();
            }}
            variant="contained"
            sx={{ 
              backgroundColor: '#FF5252',
              '&:hover': { backgroundColor: '#E53935' },
              px: 3
            }}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default ListeningTestUI;
